/**
 * Admin panel — User Games tab.
 * Lists all user-submitted games with approval workflow.
 */

import React, { useEffect, useState, useRef } from 'react';
import { Card, Button } from '@playground/ui';
import { apiFetch, apiFetchWithToken } from '@/platform/api.js';

interface UserGame {
  id: string;
  creator_id: string;
  status: string;
  meta: {
    name?: string;
    description?: string;
    minPlayers?: number;
    maxPlayers?: number;
  };
  ai_review?: {
    safe: boolean;
    notes: string;
    scannedAt: string;
  } | null;
  created_at: string;
  updated_at: string;
}

const STATUS_COLORS: Record<string, string> = {
  draft: 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300',
  pending_approval: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400',
  approved: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
  published: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
  rejected: 'bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-400',
  disabled: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400',
};

export function UserGames({ token }: { token: string }) {
  const [games, setGames] = useState<UserGame[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState<{ ok: boolean; message: string } | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [aiBuilderEnabled, setAiBuilderEnabled] = useState<boolean | null>(null);
  const [togglingBuilder, setTogglingBuilder] = useState(false);

  async function loadGames() {
    setLoading(true);
    setLoadError('');
    try {
      const res = await apiFetch('/api/admin/user-games', {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`Request failed (${res.status})`);
      const data = await res.json() as { games?: UserGame[] };
      setGames(data.games ?? []);
    } catch (err) {
      // Previously this rejected unhandled and the panel rendered
      // "No user games yet.", which reads as an empty list, not a failure.
      setLoadError(err instanceof Error ? err.message : 'Failed to load user games.');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { void loadGames(); }, [token]);

  useEffect(() => {
    if (!token) return;
    apiFetchWithToken('/api/games', token)
      .then((r) => r.json() as Promise<{ aiBuilderEnabled?: boolean }>)
      .then((d) => setAiBuilderEnabled(d.aiBuilderEnabled !== false))
      .catch(() => {});
  }, [token]);

  async function toggleAiBuilder(enabled: boolean) {
    setTogglingBuilder(true);
    try {
      await apiFetch('/api/admin/config', {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ settings: { aiBuilderEnabled: enabled } }),
      });
      setAiBuilderEnabled(enabled);
    } finally {
      setTogglingBuilder(false);
    }
  }

  /** Statuses that take a live game away from players. */
  const DESTRUCTIVE_STATUSES = new Set(['rejected', 'unpublished', 'draft']);

  async function changeStatus(id: string, status: string) {
    if (
      DESTRUCTIVE_STATUSES.has(status) &&
      !confirm(`Set "${id}" to ${status}? Players will lose access to it.`)
    ) {
      return;
    }
    try {
      const res = await apiFetch(`/api/admin/user-games/${id}/status`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ status }),
      });
      // A rejected PUT used to reload silently, snapping the row back to its
      // old status with no explanation.
      if (!res.ok) throw new Error(`Could not set status (${res.status})`);
    } catch (err) {
      setLoadError(err instanceof Error ? err.message : 'Failed to update status.');
      return;
    }
    await loadGames();
  }

  async function deleteGame(id: string) {
    if (!confirm(`Delete game "${id}"? This cannot be undone.`)) return;
    await apiFetch(`/api/admin/user-games/${id}`, {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${token}` },
    });
    await loadGames();
  }

  async function uploadZip(file: File) {
    setUploading(true);
    setUploadResult(null);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await apiFetch('/api/admin/user-games/upload', {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });
      const data = await res.json() as { id?: string; name?: string; error?: string; violations?: string[]; buildError?: string };
      if (res.ok) {
        setUploadResult({ ok: true, message: `Uploaded and published: ${data.name} (${data.id})` });
        await loadGames();
      } else {
        const detail = data.violations?.join('; ') ?? data.buildError ?? data.error ?? 'Unknown error';
        setUploadResult({ ok: false, message: detail });
      }
    } catch (err) {
      setUploadResult({ ok: false, message: String(err) });
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  }

  return (
    <div className="space-y-6">
      {/* AI Builder toggle */}
      <Card>
        <div className="flex items-center justify-between">
          <div>
            <p id="ai-builder-label" className="font-medium text-gray-900 dark:text-gray-100">AI Game Builder</p>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
              Allow users to access <code className="rounded bg-gray-100 px-1 dark:bg-gray-700">/build</code> and create their own games.
            </p>
          </div>
          <button
            type="button"
            role="switch"
            aria-checked={Boolean(aiBuilderEnabled)}
            aria-labelledby="ai-builder-label"
            disabled={togglingBuilder || aiBuilderEnabled === null}
            onClick={() => void toggleAiBuilder(!aiBuilderEnabled)}
            className={[
              'relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200',
              // `focus:outline-none` with no replacement made keyboard focus invisible.
              'focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2 disabled:opacity-50',
              aiBuilderEnabled ? 'bg-indigo-600' : 'bg-gray-300 dark:bg-gray-600',
            ].join(' ')}
          >
            <span
              className={[
                'pointer-events-none inline-block h-5 w-5 rounded-full bg-white shadow transform transition-transform duration-200',
                aiBuilderEnabled ? 'translate-x-5' : 'translate-x-0',
              ].join(' ')}
            />
          </button>
        </div>
      </Card>

      {/* Upload panel */}
      <Card>
        <h2 className="mb-3 text-base font-semibold text-gray-900 dark:text-gray-100">
          Upload Game Package
        </h2>
        <p className="mb-3 text-sm text-gray-500 dark:text-gray-400">
          Upload a ZIP file containing <code className="rounded bg-gray-100 px-1 dark:bg-gray-700">definition.ts</code>.
          The file will be compiled, security-scanned, and published immediately.
        </p>
        <div className="flex items-center gap-3">
          <input
            ref={fileInputRef}
            type="file"
            accept=".zip"
            onChange={(e) => { const f = e.target.files?.[0]; if (f) void uploadZip(f); }}
            className="hidden"
          />
          <Button
            variant="secondary"
            loading={uploading}
            onClick={() => fileInputRef.current?.click()}
          >
            {uploading ? 'Uploading…' : 'Choose ZIP file'}
          </Button>
          {uploadResult && (
            <p className={`text-sm ${uploadResult.ok ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
              {uploadResult.ok ? '✓ ' : '✗ '}{uploadResult.message}
            </p>
          )}
        </div>
      </Card>

      {/* Games list */}
      <Card>
        <div className="mb-3 flex items-center justify-between">
          <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">
            User Games {games.length > 0 && `(${games.length})`}
          </h2>
          <button
            onClick={() => void loadGames()}
            className="text-xs text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
          >
            Refresh
          </button>
        </div>

        {loading ? (
          <p role="status" className="text-sm text-gray-500 dark:text-gray-400">Loading…</p>
        ) : loadError ? (
          <div role="alert">
            <p className="text-sm text-red-700 dark:text-red-400">{loadError}</p>
            <button
              type="button"
              onClick={() => void loadGames()}
              className="mt-2 rounded-lg bg-indigo-600 px-3 py-1.5 text-sm font-semibold text-white hover:bg-indigo-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2"
            >
              Retry
            </button>
          </div>
        ) : games.length === 0 ? (
          <p className="text-sm text-gray-500 dark:text-gray-400">No user games yet.</p>
        ) : (
          <div className="space-y-3">
            {games.map((g) => (
              <div
                key={g.id}
                className="rounded-lg border border-gray-200 p-3 dark:border-gray-700"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <p className="font-medium text-gray-900 dark:text-gray-100 truncate">
                        {g.meta.name ?? g.id}
                      </p>
                      <span className={`shrink-0 rounded-full px-2 py-0.5 text-xs font-medium capitalize ${STATUS_COLORS[g.status] ?? STATUS_COLORS['draft']}`}>
                        {g.status.replace('_', ' ')}
                      </span>
                    </div>
                    <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400 font-mono">{g.id}</p>
                    {g.meta.description && (
                      <p className="mt-1 text-xs text-gray-500 dark:text-gray-400 truncate">{g.meta.description}</p>
                    )}
                    <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                      Creator: <span className="font-mono">{g.creator_id}</span>
                      {g.meta.minPlayers != null && ` · ${g.meta.minPlayers}–${g.meta.maxPlayers} players`}
                    </p>
                    {g.ai_review && (
                      <div className="mt-1.5" title={g.ai_review.notes}>
                        {g.ai_review.safe ? (
                          <span className="inline-flex items-center gap-1 rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-700 dark:bg-green-900/30 dark:text-green-400">
                            ✓ AI: Safe
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 rounded-full bg-red-100 px-2 py-0.5 text-xs font-medium text-red-700 dark:bg-red-900/30 dark:text-red-400">
                            ✗ AI: Flagged — {g.ai_review.notes.slice(0, 60)}{g.ai_review.notes.length > 60 ? '…' : ''}
                          </span>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Actions */}
                  <div className="flex shrink-0 flex-col gap-1.5">
                    {g.status === 'pending_approval' && (
                      <>
                        <button
                          onClick={() => void changeStatus(g.id, 'published')}
                          className="rounded-lg bg-green-600 px-3 py-1 text-xs font-medium text-white hover:bg-green-700 transition-colors"
                        >
                          Publish
                        </button>
                        <button
                          onClick={() => void changeStatus(g.id, 'rejected')}
                          className="rounded-lg bg-red-100 px-3 py-1 text-xs font-medium text-red-700 hover:bg-red-200 dark:bg-red-900/20 dark:text-red-400 dark:hover:bg-red-900/40 transition-colors"
                        >
                          Reject
                        </button>
                      </>
                    )}
                    {g.status === 'approved' && (
                      <button
                        onClick={() => void changeStatus(g.id, 'published')}
                        className="rounded-lg bg-green-600 px-3 py-1 text-xs font-medium text-white hover:bg-green-700 transition-colors"
                      >
                        Publish
                      </button>
                    )}
                    {g.status === 'published' && (
                      <button
                        onClick={() => void changeStatus(g.id, 'rejected')}
                        className="rounded-lg border border-gray-300 px-3 py-1 text-xs font-medium text-gray-600 hover:bg-gray-100 dark:border-gray-600 dark:text-gray-400 dark:hover:bg-gray-700 transition-colors"
                      >
                        Unpublish
                      </button>
                    )}
                    {g.status === 'rejected' && (
                      <button
                        onClick={() => void changeStatus(g.id, 'published')}
                        className="rounded-lg bg-blue-600 px-3 py-1 text-xs font-medium text-white hover:bg-blue-700 transition-colors"
                      >
                        Re-publish
                      </button>
                    )}
                    {g.status === 'disabled' && (
                      <button
                        onClick={() => void changeStatus(g.id, 'published')}
                        className="rounded-lg bg-green-600 px-3 py-1 text-xs font-medium text-white hover:bg-green-700 transition-colors"
                      >
                        Re-enable
                      </button>
                    )}
                    <button
                      onClick={() => void deleteGame(g.id)}
                      className="rounded-lg px-3 py-1 text-xs font-medium text-gray-500 dark:text-gray-400 hover:text-red-600 dark:hover:text-red-400 transition-colors"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}
